// explain — a beat of prose, with an optional ▶ demo. Demo kinds:
//   phrase → plays demo.phrase once (patch by name from PATCHES, default softKeys)
//   drone  → toggles the home drone on/off
//   track  → "your track so far": demo.phrase (or the learner's saved keeper
//            named by demo.keeperId, looped twice), layered over the drone
//            when demo.drone is true
// Always ready to continue.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { phraseLength } from "../../../lib/homeKeyTheory";
import {
  PATCHES,
  playPhrase,
  startDrone,
  resumeAudio,
} from "../../../lib/homeKeyAudio";

const resolvePatch = (patch) =>
  (typeof patch === "string" ? PATCHES[patch] : patch) || PATCHES.softKeys;

const DEMO_LABELS = {
  phrase: "hear it",
  drone: "hear the drone",
  track: "hear your track so far",
};

// Repeat a phrase back-to-back (keeper demos loop it over the drone).
const loopPhrase = (phrase, times, gapMs = 400) => {
  const len = phraseLength(phrase) + gapMs;
  const notes = [];
  for (let i = 0; i < times; i += 1) {
    (phrase.notes || []).forEach((note) => notes.push({ ...note, at: note.at + i * len }));
  }
  return { notes };
};

export default function ExplainMoment({ moment, onReadyChange, keepers }) {
  const [playing, setPlaying] = useState(false);
  const [droneOn, setDroneOn] = useState(false);
  const handleRef = useRef(null); // current { stop } for the phrase playback
  const droneRef = useRef(null); // separate so track demos can layer both
  const timerRef = useRef(null);

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const stopAll = useCallback(() => {
    if (handleRef.current && handleRef.current.stop) handleRef.current.stop();
    handleRef.current = null;
    if (droneRef.current) droneRef.current.stop();
    droneRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPlaying(false);
    setDroneOn(false);
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  const demo = moment.demo;

  // A track demo can reference one of the learner's keepers by id — resolve
  // it from whatever this run (or an earlier one) saved.
  const demoPhrase = () => {
    if (demo.phrase) return demo.phrase;
    if (demo.keeperId && keepers) {
      const kept = keepers[demo.keeperId];
      const phrase = kept && (kept.phrase || (kept.notes ? kept : null));
      if (phrase) return loopPhrase(phrase, 2);
    }
    return null;
  };

  const handleDemo = () => {
    resumeAudio();
    if (demo.kind === "drone") {
      if (droneOn) {
        stopAll();
        return;
      }
      stopAll();
      droneRef.current = startDrone();
      setDroneOn(true);
      return;
    }
    stopAll();
    const phrase = demoPhrase();
    if (!phrase) return;
    if (demo.kind === "track" && demo.drone) droneRef.current = startDrone();
    const handle = playPhrase(phrase, resolvePatch(demo.patch));
    handleRef.current = handle && handle.stop ? handle : null;
    setPlaying(true);
    const durMs = (handle && handle.durMs) || phraseLength(phrase) || 3000;
    timerRef.current = setTimeout(() => stopAll(), durMs + 400);
  };

  const body = String(moment.body || "");
  const label =
    demo && (demo.label || DEMO_LABELS[demo.kind] || DEMO_LABELS.phrase);
  const demoAvailable =
    demo && (demo.kind === "drone" || demo.phrase || (demo.keeperId && keepers));

  return (
    <div className="hk-moment-body">
      {body.split(/\n\n+/).map((para, i) => (
        <p key={i}>{para}</p>
      ))}
      {demoAvailable && (
        <button
          type="button"
          className={`hk-demo-btn${playing || droneOn ? " hk-demo-btn--on" : ""}`}
          onClick={handleDemo}
        >
          {demo.kind === "drone" && droneOn ? "◼ let it fade" : `▶ ${label}`}
        </button>
      )}
    </div>
  );
}
